/**
 * ThinClientDetector
 * Detects whether WebSocket or Deep Link communication should be used.
 *
 * Flow:
 * 1. Quick check WS (500ms) - don't block page load
 * 2. If no WS -> Deep Link immediately
 * 3. Background: retry every 10s (unlimited) until WS becomes available
 * 4. If WS becomes available -> upgrade for future requests
 *
 * The unlimited retry is important because:
 * - The ThinClient app may be started after the page has already loaded
 * - It may be turned off and on again in the meantime
 * - Once the app is running, WebSocket becomes available and we switch from Deep Link
 */

import {
	INITIAL_TIMEOUT_MS,
	MAX_RETRIES,
	RETRY_INTERVAL_MS,
	RETRY_TIMEOUT_MS,
	WS_URL,
} from './constants';

type CommunicationMode = 'websocket' | 'deeplink' | 'safari-extension';
type ModeChangeCallback = (mode: CommunicationMode) => void;

class ThinClientDetectorClass {
	private mode: CommunicationMode = 'deeplink';
	private retryInterval: ReturnType<typeof setInterval> | null = null;
	private detected = false;
	private detectionPromise: Promise<CommunicationMode> | null = null;
	private wsConnection: WebSocket | null = null;
	private modeChangeCallbacks: Set<ModeChangeCallback> = new Set();

	/**
	 * Detect which communication mode to use.
	 * Returns quickly (500ms) to not block page load.
	 */
	async detect(): Promise<CommunicationMode> {
		if (this.detected) {
			return this.mode;
		}

		if (this.detectionPromise) {
			return this.detectionPromise;
		}

		this.detectionPromise = this.performDetection();

		try {
			return await this.detectionPromise;
		} finally {
			this.detectionPromise = null;
		}
	}

	/**
	 * Perform the actual detection logic.
	 */
	private async performDetection(): Promise<CommunicationMode> {
		// Safari extension check
		if (window.__sdcThinClient?.isSafariExtensionAvailable) {
			this.mode = 'safari-extension';
			this.detected = true;
			return this.mode;
		}

		const wsResult = await this.tryWebSocket(INITIAL_TIMEOUT_MS);

		if (wsResult) {
			this.mode = 'websocket';
			this.detected = true;
			return this.mode;
		}

		this.mode = 'deeplink';
		this.detected = true;

		this.startBackgroundRetry();

		return this.mode;
	}

	/**
	 * Try to establish WebSocket connection with timeout.
	 */
	private tryWebSocket(timeoutMs: number): Promise<boolean> {
		return new Promise(resolve => {
			try {
				const ws = new WebSocket(WS_URL);
				let resolved = false;

				const timeout = setTimeout(() => {
					if (!resolved) {
						resolved = true;
						ws.close();
						resolve(false);
					}
				}, timeoutMs);

				ws.onopen = () => {
					if (!resolved) {
						resolved = true;
						clearTimeout(timeout);
						this.wsConnection = ws;
						resolve(true);
					}
				};

				ws.onerror = () => {
					if (!resolved) {
						resolved = true;
						clearTimeout(timeout);
						if (this.wsConnection === ws) {
							this.wsConnection = null;
						}
						ws.close();
						resolve(false);
					}
				};

				ws.onclose = () => {
					if (!resolved) {
						resolved = true;
						clearTimeout(timeout);
						if (this.wsConnection === ws) {
							this.wsConnection = null;
						}
						resolve(false);
					}
				};
			} catch {
				resolve(false);
			}
		});
	}

	/**
	 * Start background retry to check if WebSocket becomes available.
	 * Runs indefinitely (MAX_RETRIES = -1) or until max attempts reached.
	 */
	private startBackgroundRetry(): void {
		let attempts = 0;

		this.retryInterval = setInterval(async () => {
			if (this.getMode() === 'websocket') {
				this.stopBackgroundRetry();
				return;
			}

			attempts++;

			if (MAX_RETRIES !== -1 && attempts > MAX_RETRIES) {
				this.stopBackgroundRetry();
				return;
			}

			if (await this.tryWebSocket(RETRY_TIMEOUT_MS)) {
				const oldMode = this.mode;
				this.mode = 'websocket';
				this.stopBackgroundRetry();

				if (oldMode !== this.mode) {
					this.notifyModeChange();
				}
			}
		}, RETRY_INTERVAL_MS);
	}

	/**
	 * Stop background retry.
	 */
	private stopBackgroundRetry(): void {
		if (this.retryInterval) {
			clearInterval(this.retryInterval);
			this.retryInterval = null;
		}
	}

	/**
	 * Notify all registered callbacks about mode change.
	 */
	private notifyModeChange(): void {
		this.modeChangeCallbacks.forEach(callback => {
			try {
				callback(this.mode);
			} catch (error) {
				console.error(
					'[ThinClientDetector] Error in mode change callback:',
					error
				);
			}
		});
	}

	/**
	 * Get current communication mode.
	 */
	getMode(): CommunicationMode {
		return this.mode;
	}

	/**
	 * Check if currently using WebSocket.
	 */
	isWebSocket(): boolean {
		return this.mode === 'websocket';
	}

	/**
	 * Check if currently using Deep Link.
	 */
	isDeepLink(): boolean {
		return this.mode === 'deeplink';
	}

	/**
	 * Check if currently using Safari Extension.
	 */
	isSafariExtension(): boolean {
		return this.mode === 'safari-extension';
	}

	/**
	 * Get the cached WebSocket connection if available.
	 */
	getWebSocketConnection(): WebSocket | null {
		if (this.wsConnection && this.wsConnection.readyState === WebSocket.OPEN) {
			return this.wsConnection;
		}
		return null;
	}

	/**
	 * Set WebSocket connection (used by WebSocketThinClientInterface).
	 */
	setWebSocketConnection(ws: WebSocket | null): void {
		this.wsConnection = ws;
	}

	/**
	 * Register callback for mode changes.
	 */
	onModeChange(callback: ModeChangeCallback): () => void {
		this.modeChangeCallbacks.add(callback);
		return () => {
			this.modeChangeCallbacks.delete(callback);
		};
	}

	/**
	 * Reset detector state (useful for testing).
	 */
	reset(): void {
		this.stopBackgroundRetry();
		if (this.wsConnection) {
			this.wsConnection.close();
			this.wsConnection = null;
		}
		this.mode = 'deeplink';
		this.detected = false;
		this.detectionPromise = null;
		this.modeChangeCallbacks.clear();
	}
}

const ThinClientDetector = new ThinClientDetectorClass();

export type { CommunicationMode, ModeChangeCallback };
export { ThinClientDetector };
export default ThinClientDetector;
